import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const ProjectDropdown = ({
	projects,
	selectedProjects = [],
	loading,
	isDataStillUpdating,
	onProjectSelect,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const dropdownRef = useRef(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Filter projects based on search term
	const filteredProjects = projects.filter(project =>
		project.name.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleProjectClick = (projectId) => {
		if (projectId === 'ALL') {
			// "All projects" mode - clear specific selections
			onProjectSelect('ALL');
		} else if (projectId === 'NONE') {
			// Clear all selections
			onProjectSelect('NONE');
		} else {
			// Toggle individual project
			onProjectSelect(projectId);
		}
	};

	const getDisplayText = () => {
		if (selectedProjects.length === 0) {
			return 'All projects';
		} else if (selectedProjects.length === 1) {
			const project = projects.find(p => p.gid === selectedProjects[0]);
			return project ? project.name : 'Unknown project';
		} else if (selectedProjects.length === projects.length) {
			return 'All projects';
		} else {
			return `${selectedProjects.length} projects selected`;
		}
	};

	const isAllSelected = selectedProjects.length === 0;

	return (
		<div className='relative' ref={dropdownRef}>
			<label className='block text-sm mb-1 font-medium'>Projects</label>
			<button
				type='button'
				onClick={() => setIsOpen(!isOpen)}
				disabled={loading || isDataStillUpdating}
				className={`w-full p-2 border rounded-md text-left bg-white flex justify-between items-center ${
					loading || isDataStillUpdating ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400'
				}`}
			>
				<span className='truncate'>{getDisplayText()}</span>
				<svg
					className={`w-4 h-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
					fill='none'
					stroke='currentColor'
					viewBox='0 0 24 24'
				>
					<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
				</svg>
			</button>

			{isOpen && (
				<div className='absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-125 overflow-hidden'>
					{/* Search input */}
					<div className='p-2 border-b'>
						<input
							type='text'
							placeholder='Search projects...'
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className='w-full p-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
							autoFocus
						/>
					</div>

					{/* Options list */}
					<div className='max-h-80 overflow-y-auto'>
						{/* All projects option */}
						<button
							type='button'
							onClick={() => handleProjectClick('ALL')}
							className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center ${
								isAllSelected ? 'bg-blue-50 text-blue-700' : ''
							}`}
						>
							<span className={`w-4 h-4 mr-2 border rounded flex-shrink-0 ${
								isAllSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
							}`}>
								{isAllSelected && (
									<svg className='w-3 h-3 text-white m-0.5' fill='currentColor' viewBox='0 0 20 20'>
										<path fillRule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clipRule='evenodd' />
									</svg>
								)}
							</span>
							<span className='font-medium'>All projects</span>
						</button>

						{/* Individual project options */}
						{filteredProjects.map(project => {
							const isSelected = selectedProjects.includes(project.gid);
							return (
								<button
									key={project.gid}
									type='button'
									onClick={() => handleProjectClick(project.gid)}
									className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center ${
										isSelected ? 'bg-green-50 text-green-700' : ''
									}`}
								>
									<span className={`w-4 h-4 mr-2 border rounded flex-shrink-0 ${
										isSelected ? 'bg-green-600 border-green-600' : 'border-gray-300'
									}`}>
										{isSelected && (
											<svg className='w-3 h-3 text-white m-0.5' fill='currentColor' viewBox='0 0 20 20'>
												<path fillRule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clipRule='evenodd' />
											</svg>
										)}
									</span>
									<span className='truncate flex-1' title={project.name}>{project.name}</span>
									{project.privacy_setting && project.privacy_setting !== 'public_to_workspace' && (
										<span className='ml-2 text-xs text-gray-500 flex-shrink-0' title={`Privacy: ${project.privacy_setting}`}>
											{project.privacy_setting === 'private' ? '🔒' :
											 project.privacy_setting === 'public_to_team' ? '👥' : ''}
										</span>
									)}
								</button>
							);
						})}

						{filteredProjects.length === 0 && searchTerm && (
							<div className='px-3 py-2 text-sm text-gray-500'>
								No projects found matching "{searchTerm}"
							</div>
						)}

						{projects.length === 0 && !loading && (
							<div className='px-3 py-2 text-sm text-gray-500 space-y-2'>
								<div>No projects available</div>
								<div className='text-xs text-gray-400 border-t pt-2'>
									<strong>Note:</strong> You can only see projects where you are a member.
									Private projects and team-specific projects may not be visible even to administrators.
								</div>
							</div>
						)}
					</div>

					{/* Footer with actions */}
					<div className='border-t p-2'>
						<div className='flex justify-between items-center mb-2'>
							<button
								type='button'
								onClick={() => handleProjectClick('NONE')}
								className='text-xs text-gray-600 hover:text-gray-800'
							>
								Clear all
							</button>
							<button
								type='button'
								onClick={() => setIsOpen(false)}
								className='text-xs text-blue-600 hover:text-blue-800'
							>
								Done
							</button>
						</div>
						{filteredProjects.some(p => p.privacy_setting && p.privacy_setting !== 'public_to_workspace') && (
							<div className='text-xs text-gray-400 border-t pt-2'>
								🔒 Private • 👥 Team only
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

ProjectDropdown.propTypes = {
	projects: PropTypes.arrayOf(
		PropTypes.shape({
			gid: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
		})
	).isRequired,
	selectedProjects: PropTypes.arrayOf(PropTypes.string),
	loading: PropTypes.bool.isRequired,
	isDataStillUpdating: PropTypes.bool.isRequired,
	onProjectSelect: PropTypes.func.isRequired,
};

export default ProjectDropdown;
